import React, { useEffect, useMemo } from "react";
import { Grid } from "@mui/material";
import { makeStyles } from "@providers/Mui";
import { History } from "@containers/History";
import { ListTicketRequest } from "@functions-types";
import { StatisticsCard } from "./StatisticsCard";
import { Tickets } from "./Tickets";
import { Performance } from "./Performance";
import { useIsMobile } from "@hooks/useIsMobile";
import { useUser } from "@providers/Auth";
import { useListUserHistory } from "@cloud-functions";
import { collection, doc, limit, orderBy, query, where } from "firebase/firestore";
import { firestore } from "@@firebase";
import { useOnSnapshot } from "@hooks/useOnSnapshot";
import clsx from "clsx";
import { PendingApprovals } from "@pages/Profile/pages/Approvals";

const useStyles = makeStyles((theme) => ({
  mainContent: {
    height: "auto",
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 185px)",
    },
  },
  card: {
    height: "100%",
  },
  tableHeader: {
    paddingBottom: theme.spacing(1),
  },
  scrollTrack: {
    display: "flex",
    maxWidth: "100%",
    overflowX: "auto",
    scrollSnapType: "x mandatory",

    /* Hide scrollbar for Chrome, Safari and Opera */
    "&::-webkit-scrollbar": {
      display: "none",
    },
    /* Hide scrollbar for IE, Edge and Firefox */
    "-ms-overflow-style": "none" /* IE and Edge */,
    "scrollbar-width": "none" /* Firefox */,
  },
  statCard: {
    scrollSnapAlign: "start",
    minWidth: 210,
    flex: 1,
    "&:not(:last-child)": {
      marginRight: theme.spacing(2),
    },
  },
  history: {
    height: "100%",
    overflow: "hidden",
  },
  sidebar: {
    height: "auto",
    [theme.breakpoints.up("sm")]: {
      height: `calc(100% + ${theme.spacing(3)})`, // fix Grid spacing issue
    },
  },
  inactive: {
    opacity: 0.6,
  },
}));

const getTicketsAssignedToMeFilter = (
  userId: string,
  ticketSLAFeatureEnabled?: boolean,
): ListTicketRequest => ({
  query: {
    where: [
      ["assignee.id", "==", userId],
      ["resolved", "==", false],
    ],
    orderBy: ticketSLAFeatureEnabled ? [["deadline", "desc"]] : [["createdAt", "desc"]],
  },
});

const getTicketsCreatedByMeFilter = (
  userId: string,
  ticketSLAFeatureEnabled?: boolean,
): ListTicketRequest => ({
  query: {
    where: [["createdBy.id", "==", userId]],
    orderBy: ticketSLAFeatureEnabled ? [["deadline", "desc"]] : [["createdAt", "desc"]],
  },
});

const getTicketsICreatedForSomeoneElseFilter = (
  userId: string,
  ticketSLAFeatureEnabled?: boolean,
): ListTicketRequest => ({
  query: {
    where: [["trulyCreatedBy.id", "==", userId]],
    orderBy: ticketSLAFeatureEnabled ? [["deadline", "desc"]] : [["createdAt", "desc"]],
  },
});

const getTicketsIMWatchingFilter = (
  userId: string,
  ticketSLAFeatureEnabled?: boolean,
): ListTicketRequest => ({
  query: {
    where: [["watchersIds", "array-contains", userId]],
    orderBy: ticketSLAFeatureEnabled ? [["deadline", "desc"]] : [["createdAt", "desc"]],
  },
});

export const AgentHomePage: React.FC = () => {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const spacing = isMobile ? 2 : 3;
  const { user } = useUser();
  const [history, historyStatus] = useListUserHistory();

  const historyQuery = useMemo(() => {
    return query(
      collection(firestore, "history"),
      where("ticketOwner", "==", doc(firestore, "users", user.id)),
      orderBy("createdAt", "desc"),
      limit(1),
    );
  }, [user.id]);
  useOnSnapshot(historyQuery, () => historyStatus.refetch({}), []);

  useEffect(() => {
    if (user?.id) {
      historyStatus.refetch({});
    }
  }, []);

  return (
    <Grid container spacing={spacing}>
      <Grid item xs={12}>
        <div className={classes.scrollTrack}>
          <div className={clsx(classes.statCard, classes.inactive)}>
            <StatisticsCard title="Tickets assigned to me" />
          </div>
          <div className={clsx(classes.statCard, classes.inactive)}>
            <StatisticsCard title="Waiting for my response" />
          </div>
          <div className={clsx(classes.statCard, classes.inactive)}>
            <StatisticsCard title="Created by me" />
          </div>
          <div className={clsx(classes.statCard, classes.inactive)}>
            <StatisticsCard title="Completed by me" />
          </div>
          <div className={classes.statCard}>
            <StatisticsCard title="My rating" count={user.currentWeekRating} />
          </div>
        </div>
      </Grid>
      <Grid
        item
        xs={12}
        container
        spacing={spacing}
        className={classes.mainContent}
        direction={isMobile ? "column" : "row"}
        flexWrap="nowrap">
        <Grid item xs={12} sm={8} maxHeight="100%">
          <Tickets getFilter={getTicketsAssignedToMeFilter} title="Open tickets assigned to me" />
          <Tickets getFilter={getTicketsCreatedByMeFilter} title="Tickets I created" />
          <Tickets
            getFilter={getTicketsICreatedForSomeoneElseFilter}
            title="Tickets I created on behalf of someone else"
          />
          <Tickets getFilter={getTicketsIMWatchingFilter} title="Tickets I'm watching" />
        </Grid>
        <Grid item xs={12} sm={4} height="100%">
          <Grid
            container
            spacing={spacing}
            direction="column"
            flexWrap="nowrap"
            className={classes.sidebar}>
            <Grid item>
              <PendingApprovals
                withIcon
                withBorderColoring
                columns={["id", "createdBy", "deadline"]}
              />
            </Grid>
            <Grid item>
              <Performance />
            </Grid>
            <Grid item className={classes.history}>
              <History
                className={classes.card}
                history={history}
                loading={historyStatus.loading}
                fetchNext={() => historyStatus.fetchNext({})}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
